import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import InfoTooltip from '../components/InfoTooltip';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import api from '../utils/api';

// Format number with Danish thousand separator
const formatDKK = (num) => {
  if (num === null || num === undefined) return '0';
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Custom tooltip for hours chart
const HoursTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <div className="bg-[#1A1A1A] border border-white/20 rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium mb-2">{data?.week_label} ({data?.year})</p>
        <p className="text-[#EC4899] text-sm">
          Konsulenttimer: <span className="font-bold">{Math.round(data?.business_hours)} timer</span>
        </p>
        <p className="text-white/60 text-xs mt-2">
          {data?.week_start} - {data?.week_end}
        </p>
      </div>
    );
  }
  return null;
};

// Custom tooltip for dækning chart
const DaekningTooltip = ({ active, payload, dpt }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <div className="bg-[#1A1A1A] border border-white/20 rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium mb-2">{data?.week_label} ({data?.year})</p>
        <p className="text-[#8B5CF6] text-sm">
          Forventet dækning: <span className="font-bold">{formatDKK(data?.expected_daekning)} kr</span>
        </p>
        <p className="text-white/60 text-xs mt-2">
          {Math.round(data?.business_hours)} timer × {formatDKK(dpt)} kr/time
        </p>
      </div>
    );
  }
  return null;
};

// Custom tooltip for bookinger chart
const BookingerTooltip = ({ active, payload, kpt, aflysningsPct }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    return (
      <div className="bg-[#1A1A1A] border border-white/20 rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium mb-2">{data?.week_label} ({data?.year})</p>
        <p className="text-[#F59E0B] text-sm">
          Bookingbehov: <span className="font-bold">{Math.round(data?.required_bookinger)}</span>
        </p>
        <p className="text-[#EF4444] text-sm">
          Inkl. {aflysningsPct}% aflysninger: <span className="font-bold">{Math.round(data?.med_aflysninger)}</span>
        </p>
        <p className="text-white/60 text-xs mt-2">
          {Math.round(data?.business_hours)} timer × {kpt} KPT × {(1 + aflysningsPct / 100).toFixed(2)}
        </p>
      </div>
    );
  }
  return null;
};

// Forventet Bookinger Chart Component
const ForventetBookingerChart = ({ data, kpt, aflysningsPct }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxBookinger = Math.max(...data.map(d => d.med_aflysninger || 0)) * 1.2;
  const avgMedAflysninger = data.length > 0 
    ? Math.round(data.reduce((sum, d) => sum + (d.med_aflysninger || 0), 0) / data.length) 
    : 0;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="week_label"
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
        />
        <YAxis 
          domain={[0, maxBookinger]}
          stroke="#F59E0B" 
          tick={{ fill: '#F59E0B', fontSize: 11 }}
          tickFormatter={(v) => Math.round(v)}
          width={50}
          label={{ value: 'Bookinger', angle: -90, position: 'insideLeft', fill: '#F59E0B', fontSize: 12 }}
        />
        <Tooltip content={<BookingerTooltip kpt={kpt} aflysningsPct={aflysningsPct} />} />
        {avgMedAflysninger > 0 && (
          <ReferenceLine 
            y={avgMedAflysninger} 
            stroke="rgba(239,68,68,0.7)" 
            strokeDasharray="5 5"
            strokeWidth={2}
            label={{ value: `▸ ${avgMedAflysninger}`, position: 'left', fill: '#EF4444', fontSize: 12, fontWeight: 700 }}
          />
        )}
        <Line 
          type="monotone" 
          dataKey="required_bookinger" 
          name="Bookingbehov"
          stroke="#F59E0B" 
          strokeWidth={2}
          strokeDasharray="5 5"
          dot={{ fill: '#F59E0B', strokeWidth: 1, r: 3 }}
          activeDot={{ r: 5 }}
        />
        <Line 
          type="monotone" 
          dataKey="med_aflysninger" 
          name="Inkl. aflysninger"
          stroke="#EF4444" 
          strokeWidth={3}
          dot={{ fill: '#EF4444', strokeWidth: 2, r: 5 }}
          activeDot={{ r: 7 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Prognose Line Chart Component (Hours)
const PrognoseLineChart = ({ data, avgHours }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxHours = Math.max(...data.map(d => d.business_hours || 0)) * 1.2;
  const minHours = Math.min(...data.map(d => d.business_hours || 0)) * 0.8;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="week_label"
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
        />
        <YAxis 
          domain={[minHours, maxHours]}
          stroke="#EC4899" 
          tick={{ fill: '#EC4899', fontSize: 11 }}
          tickFormatter={(v) => Math.round(v)}
          width={50}
          label={{ value: 'Timer', angle: -90, position: 'insideLeft', fill: '#EC4899', fontSize: 12 }}
        />
        <Tooltip content={<HoursTooltip />} />
        {avgHours > 0 && (
          <ReferenceLine 
            y={avgHours} 
            stroke="rgba(236,72,153,0.5)" 
            strokeDasharray="5 5"
            label={{ value: `Gns: ${Math.round(avgHours)}`, position: 'right', fill: 'rgba(236,72,153,0.7)', fontSize: 10 }}
          />
        )}
        <Line 
          type="monotone" 
          dataKey="business_hours" 
          name="Konsulenttimer"
          stroke="#EC4899" 
          strokeWidth={3}
          dot={{ fill: '#EC4899', strokeWidth: 2, r: 5 }}
          activeDot={{ r: 7 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Forventet Dækning Chart Component
const ForventetDaekningChart = ({ data, dpt }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxDaekning = Math.max(...data.map(d => d.expected_daekning || 0)) * 1.2;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="week_label"
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
        />
        <YAxis 
          domain={[0, maxDaekning]}
          stroke="#8B5CF6" 
          tick={{ fill: '#8B5CF6', fontSize: 11 }}
          tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : `${Math.round(v/1000)}k`}
          width={55}
          label={{ value: 'Dækning (kr)', angle: -90, position: 'insideLeft', fill: '#8B5CF6', fontSize: 12 }}
        />
        <Tooltip content={<DaekningTooltip dpt={dpt} />} />
        <Line 
          type="monotone" 
          dataKey="expected_daekning" 
          name="Forventet dækning"
          stroke="#8B5CF6" 
          strokeWidth={3}
          dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 5 }}
          activeDot={{ r: 7 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const Prognose = () => {
  const navigate = useNavigate();
  const [region, setRegion] = useState('Hele landet');
  const [dpt, setDpt] = useState(1700);
  const [kpt, setKpt] = useState(0.5);
  const [aflysningsPct, setAflysningsPct] = useState(10);
  const regions = ['Hele landet', 'Sjælland', 'Fyn', 'Jylland'];
  
  const [prognoseData, setPrognoseData] = useState(null);
  const [absenceData, setAbsenceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const regionParam = region !== 'Hele landet' ? `&region=${encodeURIComponent(region)}` : '';
      
      const [hoursResponse, absenceResponse] = await Promise.all([
        api.get(`/prognose/consultant-hours?weeks=10${regionParam}`),
        api.get(`/prognose/absences?weeks=10${regionParam}`)
      ]);
      
      setPrognoseData(hoursResponse.data);
      setAbsenceData(absenceResponse.data);
    } catch (err) {
      console.error('Data fetch error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Kunne ikke hente data. Prøv igen senere.');
      }
    } finally {
      setLoading(false);
    }
  }, [region, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate expected dækning based on hours and DPT
  const daekningData = useMemo(() => {
    if (!prognoseData?.weekly_data) return [];
    
    return prognoseData.weekly_data.map(week => ({
      ...week,
      expected_daekning: (week.business_hours || 0) * dpt
    }));
  }, [prognoseData, dpt]);

  // Calculate total expected dækning
  const totalExpectedDaekning = useMemo(() => {
    return daekningData.reduce((sum, week) => sum + (week.expected_daekning || 0), 0);
  }, [daekningData]);

  // Calculate required bookinger based on hours, KPT and aflysningsprocent
  const bookingerData = useMemo(() => {
    if (!prognoseData?.weekly_data) return [];
    
    return prognoseData.weekly_data.map(week => {
      const baseBookinger = (week.business_hours || 0) * kpt;
      const inklusivAflysninger = baseBookinger * (1 + aflysningsPct / 100);
      return {
        ...week,
        required_bookinger: Math.round(baseBookinger),
        med_aflysninger: Math.round(inklusivAflysninger)
      };
    });
  }, [prognoseData, kpt, aflysningsPct]);

  return (
    <div className="min-h-screen bg-[#1A1A1A]">
      {/* Header */}
      <header className="bg-[#1A1A1A] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Navigation />
            <h1 className="text-xl font-semibold text-white">Prognose</h1>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 text-white text-sm px-4 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
              data-testid="region-filter"
            >
              {regions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
      </header>
      
      <main className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/60">Indlæser data...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-400">{error}</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Konsulenttimer Chart */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-white">Konsulenttimer de næste 10 uger</h2>
                  <InfoTooltip
                    title="Konsulenttimer (Prognose)"
                    description="Viser forventede arbejdstimer for konsulenter de næste 10 uger baseret på kalenderen."
                    formula="Timer = Sum af business_hours_min ÷ 60"
                    details={[
                      "Kun 'Konsulent' og 'Konsulent/montør' indgår",
                      "Konsulent/montør tælles med 60% af deres tid",
                      "Planlagt fravær (ferie, barsel) er fratrukket",
                      "Baseret på data i kalenderen fremadrettet"
                    ]}
                    dataSource="CalendarTypeCounts (Data_klar_1)"
                  />
                </div>
                <PrognoseLineChart 
                  data={prognoseData?.weekly_data} 
                  avgHours={prognoseData?.summary?.avg_hours_per_week}
                />
              </div>

              {/* Nødvendige Bookinger Chart */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-white">Nødvendige bookinger pr. uge</h2>
                  <InfoTooltip
                    title="Nødvendige bookinger"
                    description="Beregner hvor mange bookinger der skal til pr. uge baseret på konsulenttimer og KPT (Kunder pr. time)."
                    formula="Bookinger = Konsulenttimer × KPT"
                    details={[
                      "Juster KPT med skyderen for at se forskellige scenarier",
                      "Standard KPT er 0,50 kunder pr. time",
                      "Bruges til at planlægge booking-mål"
                    ]}
                    dataSource="Beregnet fra konsulenttimer"
                  />
                </div>
                
                {/* KPT Slider */}
                <div className="mb-4 p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-white/80">KPT (Kunder pr. time)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={kpt}
                        onChange={(e) => setKpt(Math.max(0, parseFloat(e.target.value) || 0))}
                        step="0.01"
                        className="w-20 bg-[#1A1A1A] border border-white/20 text-white text-sm px-3 py-1 rounded-lg text-right focus:outline-none focus:border-[#F59E0B]"
                        data-testid="kpt-input"
                      />
                      <span className="text-white/60 text-sm">kunder/time</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0.2"
                    max="1.0"
                    step="0.01"
                    value={kpt}
                    onChange={(e) => setKpt(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#F59E0B]"
                    data-testid="kpt-slider"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>0,20</span>
                    <span>0,50</span>
                    <span>1,00</span>
                  </div>
                </div>
                
                {/* Aflysningsprocent Slider */}
                <div className="mb-4 p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-white/80">Aflysningsprocent</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={aflysningsPct}
                        onChange={(e) => setAflysningsPct(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))}
                        className="w-16 bg-[#1A1A1A] border border-white/20 text-white text-sm px-3 py-1 rounded-lg text-right focus:outline-none focus:border-[#EF4444]"
                        data-testid="aflysning-input"
                      />
                      <span className="text-white/60 text-sm">%</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="1"
                    value={aflysningsPct}
                    onChange={(e) => setAflysningsPct(parseInt(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#EF4444]"
                    data-testid="aflysning-slider"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>0%</span>
                    <span>15%</span>
                    <span>30%</span>
                  </div>
                </div>
                
                <ForventetBookingerChart data={bookingerData} kpt={kpt} aflysningsPct={aflysningsPct} />
              </div>

              {/* Forventet Dækning Chart */}
              <div className="bg-white/5 rounded-xl p-5 lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-white">Forventet dækning</h2>
                  <InfoTooltip
                    title="Forventet dækning"
                    description="Beregner forventet dækning baseret på konsulenttimer ganget med DPT (Dækning pr. time)."
                    formula="Forventet dækning = Konsulenttimer × DPT"
                    details={[
                      "Juster DPT med skyderen for at se forskellige scenarier",
                      "Standard DPT er 1.700 kr/time",
                      "Bruges til budgettering og forecasting"
                    ]}
                    dataSource="Beregnet fra konsulenttimer"
                  />
                </div>
                
                {/* DPT Slider */}
                <div className="mb-4 p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-white/80">DPT (Dækning pr. time)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={dpt}
                        onChange={(e) => setDpt(Math.max(0, parseInt(e.target.value) || 0))}
                        className="w-24 bg-[#1A1A1A] border border-white/20 text-white text-sm px-3 py-1 rounded-lg text-right focus:outline-none focus:border-[#8B5CF6]"
                      />
                      <span className="text-white/60 text-sm">kr/time</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="2500"
                    step="50"
                    value={dpt}
                    onChange={(e) => setDpt(parseInt(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#8B5CF6]"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>1.000 kr</span>
                    <span>1.750 kr</span>
                    <span>2.500 kr</span>
                  </div>
                </div>
                
                <ForventetDaekningChart data={daekningData} dpt={dpt} />
              </div>
            </div>

            {/* Fraværsliste - sorteret efter uger */}
            {absenceData?.absences && absenceData.absences.length > 0 && (
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-white">Planlagt fravær de næste 10 uger</h2>
                  <InfoTooltip
                    title="Planlagt fravær"
                    description="Viser hvilke konsulenter der har planlagt fravær, grupperet efter uge. Inkluderer også helligdage."
                    details={[
                      "Inkluderer ferie, sygdom, barsel og barns sygedag",
                      "Helligdage vises med lilla markering",
                      "Fraværet er allerede fratrukket i konsulenttimer-grafen",
                      "Sorteret efter uge for let sammenligning med grafen"
                    ]}
                    dataSource="Data_klar_1, Helligdage"
                  />
                </div>
                
                {/* Summary */}
                <div className="flex gap-4 mb-4 text-sm flex-wrap">
                  <div className="bg-white/5 rounded-lg px-4 py-2">
                    <span className="text-white/60">Konsulenter med fravær: </span>
                    <span className="text-white font-medium">{absenceData.summary?.total_consultants_absent || 0}</span>
                  </div>
                  <div className="bg-white/5 rounded-lg px-4 py-2">
                    <span className="text-white/60">Fraværsdage i alt: </span>
                    <span className="text-white font-medium">{absenceData.summary?.total_absence_days || 0}</span>
                  </div>
                  {absenceData.summary?.total_holiday_days > 0 && (
                    <div className="bg-purple-500/10 rounded-lg px-4 py-2">
                      <span className="text-purple-300/80">Helligdage: </span>
                      <span className="text-purple-300 font-medium">{absenceData.summary?.total_holiday_days}</span>
                    </div>
                  )}
                </div>
                
                {/* Holidays Section */}
                {absenceData.holidays && absenceData.holidays.length > 0 && (
                  <div className="mb-4 bg-purple-500/10 rounded-lg p-4">
                    <h3 className="text-purple-300 font-medium mb-3">Helligdage i perioden</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {absenceData.holidays.map((week, wIdx) => (
                        <div key={wIdx} className="bg-white/5 rounded-lg p-3">
                          <div className="text-white/60 text-sm mb-1">{week.week_label}</div>
                          {week.holidays.map((holiday, hIdx) => (
                            <div key={hIdx} className="flex items-center justify-between">
                              <span className="text-purple-300">{holiday.name}</span>
                              <span className="text-white/50 text-xs">{holiday.weekday} {holiday.date.split('-').reverse().join('/')}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Absence by Week */}
                <div className="space-y-4">
                  {(() => {
                    // Transform data: group by week instead of consultant
                    const weekMap = {};
                    absenceData.absences.forEach(consultant => {
                      consultant.weeks.forEach(week => {
                        if (!weekMap[week.week_key]) {
                          weekMap[week.week_key] = {
                            week_key: week.week_key,
                            week_number: week.week_number,
                            week_label: week.week_label,
                            consultants: []
                          };
                        }
                        weekMap[week.week_key].consultants.push({
                          name: consultant.name,
                          region: consultant.region,
                          absence_types: week.absence_types,
                          days_absent: week.days_absent
                        });
                      });
                    });
                    
                    // Sort weeks and return
                    const sortedWeeks = Object.values(weekMap).sort((a, b) => a.week_key.localeCompare(b.week_key));
                    
                    return sortedWeeks.map((week, wIdx) => (
                      <div key={wIdx} className="bg-white/5 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-white font-medium">{week.week_label}</h3>
                          <span className="text-white/60 text-sm">{week.consultants.length} konsulent{week.consultants.length !== 1 ? 'er' : ''}</span>
                        </div>
                        <div className="space-y-2">
                          {week.consultants.map((consultant, cIdx) => (
                            <div key={cIdx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                              <div className="flex items-center gap-3">
                                <span className="text-white">{consultant.name}</span>
                                <span className="text-white/50 text-sm">{consultant.region}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {consultant.absence_types.map((type, tIdx) => (
                                  <span 
                                    key={tIdx} 
                                    className={`px-2 py-0.5 rounded text-xs ${
                                      type === 'Ferie' ? 'bg-blue-500/20 text-blue-300' :
                                      type === 'Sygdom' ? 'bg-red-500/20 text-red-300' :
                                      type === 'Barsel' ? 'bg-green-500/20 text-green-300' :
                                      'bg-orange-500/20 text-orange-300'
                                    }`}
                                  >
                                    {type}
                                  </span>
                                ))}
                                <span className="text-white/60 text-sm ml-2">{consultant.days_absent} dag{consultant.days_absent !== 1 ? 'e' : ''}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* No absences message */}
            {absenceData?.absences && absenceData.absences.length === 0 && (
              <div className="bg-white/5 rounded-xl p-5">
                <h2 className="text-lg font-semibold text-white mb-4">Planlagt fravær de næste 10 uger</h2>
                
                {/* Still show holidays even if no consultant absences */}
                {absenceData.holidays && absenceData.holidays.length > 0 && (
                  <div className="mb-4 bg-purple-500/10 rounded-lg p-4">
                    <h3 className="text-purple-300 font-medium mb-3">Helligdage i perioden</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {absenceData.holidays.map((week, wIdx) => (
                        <div key={wIdx} className="bg-white/5 rounded-lg p-3">
                          <div className="text-white/60 text-sm mb-1">{week.week_label}</div>
                          {week.holidays.map((holiday, hIdx) => (
                            <div key={hIdx} className="flex items-center justify-between">
                              <span className="text-purple-300">{holiday.name}</span>
                              <span className="text-white/50 text-xs">{holiday.weekday} {holiday.date.split('-').reverse().join('/')}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <p className="text-white/60 text-sm">Ingen konsulenter har planlagt fravær i de næste 10 uger.</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Prognose;
